import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, X, PenLine, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useReveal } from "../../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stars = ({ value = 5, size = 14, className = "" }) => (
  <div className={`flex gap-1 ${className}`} aria-label={`${value} star rating`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        size={size}
        className={i < value ? "fill-current text-gold" : "text-gold/30"}
      />
    ))}
  </div>
);

const RatingPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div
      className="flex items-center gap-1"
      data-testid="review-rating"
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`review-star-${n}`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className="p-1 -m-1 transition-colors"
        >
          <Star
            size={24}
            className={
              n <= (hover || value)
                ? "fill-current text-gold"
                : "text-border-soft"
            }
            strokeWidth={1.4}
          />
        </button>
      ))}
    </div>
  );
};

const initial = {
  name: "",
  rating: 5,
  message: "",
  session_type: "",
  location: "",
};

const inputBase =
  "w-full bg-white border border-border-soft px-4 py-3 text-sm text-accent-dark font-sans focus:outline-none focus:border-gold transition-colors placeholder:text-text-muted/60";
const labelBase =
  "block text-[11px] tracking-[0.2em] uppercase text-text-muted font-medium mb-2";

const ReviewFormModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const tt = t.trust;
  const [values, setValues] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setValues(initial);
      setStatus("idle");
      setErr("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const update = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErr("");
    try {
      await axios.post(`${API}/reviews`, {
        ...values,
        session_type: values.session_type || null,
        location: values.location || null,
      });
      setStatus("success");
    } catch (er) {
      setStatus("error");
      setErr(
        er?.response?.data?.detail && typeof er.response.data.detail === "string"
          ? er.response.data.detail
          : tt.errorGeneric
      );
    }
  };

  return (
    <div
      data-testid="review-modal"
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-bg-base w-full max-w-2xl relative my-auto">
        <button
          onClick={onClose}
          data-testid="review-modal-close"
          aria-label={tt.cancel}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center text-text-muted hover:text-accent-dark transition-colors"
        >
          <X size={20} />
        </button>

        {status === "success" ? (
          <div className="p-10 md:p-14 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-bg-alt flex items-center justify-center text-gold">
              <CheckCircle2 size={28} strokeWidth={1.4} />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-accent-dark mt-6 font-light">
              {tt.successTitle}
            </h3>
            <p className="mt-4 text-text-muted leading-relaxed max-w-md mx-auto">
              {tt.successBody}
            </p>
            <button
              onClick={onClose}
              data-testid="review-close-after-success"
              className="btn-luxury btn-luxury-dark mt-8"
              style={{ padding: "0.9rem 1.75rem", fontSize: "0.7rem" }}
            >
              {tt.cancel}
            </button>
          </div>
        ) : (
          <form
            data-testid="review-form"
            onSubmit={submit}
            className="p-8 md:p-10 space-y-6"
          >
            <div>
              <p className="overline">{tt.overline}</p>
              <h2 className="font-serif text-2xl md:text-3xl text-accent-dark mt-3 font-light leading-tight">
                {tt.formTitle}
              </h2>
              <p className="mt-3 text-sm text-text-muted leading-relaxed">
                {tt.formSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelBase}>
                  {tt.fields.rating} <span className="text-gold">*</span>
                </label>
                <RatingPicker
                  value={values.rating}
                  onChange={(v) => setValues((s) => ({ ...s, rating: v }))}
                />
              </div>
              <div>
                <label className={labelBase} htmlFor="rf-name">
                  {tt.fields.name} <span className="text-gold">*</span>
                </label>
                <input
                  id="rf-name"
                  data-testid="rf-name"
                  type="text"
                  required
                  minLength={2}
                  maxLength={80}
                  value={values.name}
                  onChange={update("name")}
                  placeholder={tt.placeholders.name}
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase} htmlFor="rf-session">
                  {tt.fields.sessionType}
                </label>
                <input
                  id="rf-session"
                  data-testid="rf-session"
                  type="text"
                  maxLength={80}
                  value={values.session_type}
                  onChange={update("session_type")}
                  placeholder={tt.placeholders.session}
                  className={inputBase}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelBase} htmlFor="rf-location">
                  {tt.fields.location}
                </label>
                <input
                  id="rf-location"
                  data-testid="rf-location"
                  type="text"
                  maxLength={120}
                  value={values.location}
                  onChange={update("location")}
                  placeholder={tt.placeholders.location}
                  className={inputBase}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelBase} htmlFor="rf-message">
                  {tt.fields.message} <span className="text-gold">*</span>
                </label>
                <textarea
                  id="rf-message"
                  data-testid="rf-message"
                  rows={5}
                  required
                  minLength={10}
                  maxLength={1200}
                  value={values.message}
                  onChange={update("message")}
                  placeholder={tt.placeholders.message}
                  className={`${inputBase} resize-none`}
                />
              </div>
            </div>

            {status === "error" && err && (
              <p
                data-testid="review-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3"
              >
                {err}
              </p>
            )}

            <button
              type="submit"
              data-testid="rf-submit"
              disabled={status === "sending"}
              className="btn-luxury disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ padding: "1rem 2rem", fontSize: "0.7rem" }}
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> {tt.sending}
                </>
              ) : (
                <>
                  {tt.submit} <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const Trust = () => {
  const { t } = useLanguage();
  const ref = useReveal();
  const [remote, setRemote] = useState([]);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then((r) => {
        if (alive) setRemote(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Merge approved remote reviews first; fill up to 3 with defaults.
  const displayed = [
    ...remote.map((r) => ({
      quote: r.message,
      name: r.name,
      tag: [r.session_type, r.location].filter(Boolean).join(" · ") || "Client review",
      rating: r.rating,
    })),
    ...t.trust.reviews.map((r) => ({ ...r, rating: 5 })),
  ].slice(0, 3);

  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="bg-bg-base py-16 md:py-20"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="overline">{t.trust.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-accent-dark mt-4 tracking-tight font-light">
            {t.trust.title}
          </h2>
          <p className="mt-5 text-text-muted text-base md:text-lg leading-relaxed">
            {t.trust.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {displayed.map((r, i) => (
            <article
              key={i}
              data-testid={`testimonial-${i}`}
              className="bg-white border border-border-soft p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
            >
              <Stars value={r.rating || 5} />
              <blockquote className="font-serif text-xl md:text-2xl text-accent-dark leading-snug mt-6 font-light">
                “{r.quote}”
              </blockquote>
              <div className="mt-8 flex items-center gap-3">
                <span className="hairline" />
                <div>
                  <p className="text-sm font-medium text-accent-dark">{r.name}</p>
                  <p className="text-xs text-text-muted tracking-wide">{r.tag}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => setModal(true)}
            data-testid="open-review-form"
            className="btn-luxury btn-luxury-dark"
            style={{ padding: "1rem 2rem", fontSize: "0.7rem" }}
          >
            <PenLine size={14} /> {t.trust.shareCta}
          </button>
        </div>
      </div>

      <ReviewFormModal open={modal} onClose={() => setModal(false)} />
    </section>
  );
};
